import React, { useState, useEffect } from 'react';
import { superAdminApi, demoLeadApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Buildings, Users, Shield, Database, ClockCounterClockwise, Plus, Trash, PencilSimple, LockKey, UserCircleMinus, UserCirclePlus, ArrowClockwise, UserList, Key } from '@phosphor-icons/react';

const TABS = [
  { key: 'tenants', label: 'Tenants', icon: Buildings },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'demo_leads', label: 'Demo Leads', icon: UserList },
  { key: 'backups', label: 'Backups', icon: Database },
  { key: 'audit', label: 'Audit Log', icon: ClockCounterClockwise },
];

export default function SuperAdminPage() {
  const [tab, setTab] = useState('tenants');

  return (
    <div className="space-y-4" data-testid="superadmin-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A]">Super Admin Control Panel</h1>
        <p className="text-sm text-[#666]">SGK Apps — Tenant management, users, backups & audit</p>
      </div>
      <div className="flex gap-1 border-b border-[#E0E0E0]" data-testid="sa-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? 'border-[#1A1F36] text-[#1A1F36]' : 'border-transparent text-[#888] hover:text-[#555]'
            }`}
            data-testid={`sa-tab-${t.key}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>
      {tab === 'tenants' && <TenantsTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'demo_leads' && <DemoLeadsTab />}
      {tab === 'backups' && <BackupsTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}

// ==================== DEMO LEADS TAB ====================
function DemoLeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    demoLeadApi.list()
      .then(res => setLeads(res.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-6"><Spinner size="lg" /></div>;

  return (
    <Card className="p-4" data-testid="demo-leads-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Demo Requests ({leads.length})</h2>
        <p className="text-xs text-[#999]">Contact details collected from website visitors</p>
      </div>
      {leads.length === 0 ? (
        <div className="text-center text-[#888] py-6">No demo requests yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="demo-leads-table">
            <thead>
              <tr className="border-b border-[#E0E0E0] text-left text-[#666] text-xs">
                <th className="pb-2 pr-4">Name</th>
                <th className="pb-2 pr-4">Company</th>
                <th className="pb-2 pr-4">Phone</th>
                <th className="pb-2 pr-4">Email</th>
                <th className="pb-2 pr-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr key={lead.id} className="border-b border-[#F0F0F0] hover:bg-[#F9F9F9]" data-testid={`lead-row-${lead.id}`}>
                  <td className="py-2 pr-4 font-medium">{lead.name}</td>
                  <td className="py-2 pr-4">{lead.company}</td>
                  <td className="py-2 pr-4 font-mono text-xs">{lead.phone}</td>
                  <td className="py-2 pr-4">
                    <a href={`mailto:${lead.email}`} className="text-[#002FA7] hover:underline">{lead.email}</a>
                  </td>
                  <td className="py-2 pr-4 text-xs text-[#999]">{new Date(lead.requested_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ==================== TENANTS TAB ====================
function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [form, setForm] = useState({ company_name: '', trn: '', admin_email: '', admin_name: '', admin_password: '' });
  const [subForm, setSubForm] = useState({ subscription_start: '', subscription_end: '', subscription_plan: 'Annual', subscription_notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await superAdminApi.listTenants(); setTenants(r.data); }
    catch {} finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try { await superAdminApi.createTenant(form); setShowModal(false); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleSuspend = async (id) => {
    if (!window.confirm('Suspend this tenant? All users will be locked out.')) return;
    try { await superAdminApi.suspendTenant(id); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleActivate = async (id) => {
    try { await superAdminApi.updateTenant(id, { status: 'active' }); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const openSubModal = (tenant) => {
    setSelectedTenant(tenant);
    setSubForm({
      subscription_start: tenant.subscription_start ? tenant.subscription_start.split('T')[0] : new Date().toISOString().split('T')[0],
      subscription_end: tenant.subscription_end ? tenant.subscription_end.split('T')[0] : '',
      subscription_plan: tenant.subscription_plan || 'Annual',
      subscription_notes: tenant.subscription_notes || '',
    });
    setShowSubModal(true);
  };

  const handleSubSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await superAdminApi.updateTenant(selectedTenant.id, subForm);
      setShowSubModal(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const isExpired = (t) => {
    if (!t.subscription_end) return false;
    return new Date(t.subscription_end) < new Date();
  };

  const daysLeft = (t) => {
    if (!t.subscription_end) return null;
    const diff = Math.ceil((new Date(t.subscription_end) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-3" data-testid="tenants-tab">
      <div className="flex justify-between items-center">
        <span className="text-sm font-semibold text-[#1A1F36]">{tenants.length} Tenant(s)</span>
        <Button size="sm" onClick={() => { setForm({ company_name: '', trn: '', admin_email: '', admin_name: '', admin_password: '' }); setError(''); setShowModal(true); }}
          data-testid="create-tenant-btn"><Plus className="w-4 h-4 mr-1" /> New Tenant</Button>
      </div>
      {tenants.map(t => {
        const days = daysLeft(t);
        const expired = isExpired(t);
        return (
        <Card key={t.id} className="p-4" data-testid={`tenant-${t.id}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#1A1F36]/10 flex items-center justify-center">
                <Buildings className="w-5 h-5 text-[#1A1F36]" />
              </div>
              <div>
                <p className="font-semibold text-sm">{t.company_name}</p>
                <div className="flex gap-2 text-xs text-[#888] flex-wrap">
                  {t.trn && <span>TRN: {t.trn}</span>}
                  <span>{t.user_count} user(s)</span>
                  <span>{t.created_at ? new Date(t.created_at).toLocaleDateString('en-US') : ''}</span>
                </div>
                {t.subscription_end && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs font-medium ${expired ? 'text-[#FF3333]' : days !== null && days <= 30 ? 'text-[#FFAB00]' : 'text-[#00C853]'}`}>
                      {expired ? 'EXPIRED' : days !== null ? `${days} days left` : ''}
                    </span>
                    <span className="text-[10px] text-[#999]">
                      {t.subscription_plan || 'Annual'} | Ends: {new Date(t.subscription_end).toLocaleDateString('en-US')}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={t.status === 'active' ? 'success' : 'danger'}>{t.status}</Badge>
              <button onClick={() => openSubModal(t)} className="p-1.5 text-[#888] hover:text-[#002FA7] hover:bg-[#F0F4FF] rounded" title="Manage Subscription" data-testid={`sub-${t.id}`}>
                <Shield className="w-4 h-4" />
              </button>
              {t.status === 'active' ? (
                <button onClick={() => handleSuspend(t.id)} className="p-1.5 text-[#888] hover:text-[#FF3333] hover:bg-[#FEF2F2] rounded" title="Suspend" data-testid={`suspend-${t.id}`}>
                  <UserCircleMinus className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={() => handleActivate(t.id)} className="p-1.5 text-[#888] hover:text-[#00C853] hover:bg-[#E8F5E9] rounded" title="Activate" data-testid={`activate-${t.id}`}>
                  <UserCirclePlus className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </Card>
        );
      })}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Create New Tenant">
        <form onSubmit={handleCreate} className="space-y-3" data-testid="create-tenant-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <Input label="Company Name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required data-testid="tenant-company" />
          <Input label="TRN (Tax Reg. Number)" value={form.trn} onChange={(e) => setForm({ ...form, trn: e.target.value })} data-testid="tenant-trn" />
          <hr className="border-[#E8E8ED]" />
          <p className="text-xs text-[#888] font-medium">Tenant Admin Account</p>
          <Input label="Admin Name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} required data-testid="tenant-admin-name" />
          <Input label="Admin Email" type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} required data-testid="tenant-admin-email" />
          <Input label="Admin Password" type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} required data-testid="tenant-admin-pass" />
          <div className="flex justify-end gap-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-tenant-btn">{saving ? 'Creating...' : 'Create Tenant'}</Button>
          </div>
        </form>
      </Modal>

      {/* Subscription Management Modal */}
      <Modal isOpen={showSubModal} onClose={() => setShowSubModal(false)} title={`Subscription - ${selectedTenant?.company_name || ''}`}>
        <form onSubmit={handleSubSave} className="space-y-3" data-testid="subscription-form">
          <Select label="Plan" value={subForm.subscription_plan} onChange={(e) => setSubForm({ ...subForm, subscription_plan: e.target.value })} data-testid="sub-plan">
            <option value="Annual">Annual (12 months)</option>
            <option value="Semi-Annual">Semi-Annual (6 months)</option>
            <option value="Quarterly">Quarterly (3 months)</option>
            <option value="Custom">Custom</option>
          </Select>
          <Input label="Start Date" type="date" value={subForm.subscription_start} onChange={(e) => setSubForm({ ...subForm, subscription_start: e.target.value })} required data-testid="sub-start" />
          <Input label="End Date" type="date" value={subForm.subscription_end} onChange={(e) => setSubForm({ ...subForm, subscription_end: e.target.value })} required data-testid="sub-end" />
          <div>
            <label className="block text-xs font-medium text-[#555] mb-1">Notes</label>
            <textarea value={subForm.subscription_notes} onChange={(e) => setSubForm({ ...subForm, subscription_notes: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-[#E0E0E0] rounded-lg" rows={2} placeholder="Payment ref, bank transfer date, etc." data-testid="sub-notes" />
          </div>
          <div className="bg-[#FFF8E1] p-3 rounded-lg text-xs text-[#B8860B]">
            When subscription expires, you can suspend the tenant from the main list. Suspended tenants see a "Service Paused" screen.
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowSubModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-sub-btn">{saving ? 'Saving...' : 'Save Subscription'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ==================== USERS TAB ====================
function UsersTab() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [otpResult, setOtpResult] = useState(null);
  const [otpLoading, setOtpLoading] = useState(null);

  useEffect(() => {
    superAdminApi.listTenants().then(r => setTenants(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadUsers(); }, [selectedTenant]);

  const loadUsers = async () => {
    try {
      const r = await superAdminApi.listUsers(selectedTenant || undefined);
      setUsers(r.data);
    } catch {}
  };

  const handleRoleChange = async (userId, role) => {
    try { await superAdminApi.updateUser(userId, { role }); loadUsers(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDisable = async (userId) => {
    try { await superAdminApi.disableUser(userId); loadUsers(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleEnable = async (userId) => {
    try { await superAdminApi.enableUser(userId); loadUsers(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword) return;
    setResetSaving(true);
    try { await superAdminApi.resetPassword(resetUserId, newPassword); setShowResetModal(false); setNewPassword(''); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setResetSaving(false); }
  };

  const handleGenerateOtp = async (userId) => {
    setOtpLoading(userId);
    try {
      const res = await superAdminApi.generateOtp(userId);
      setOtpResult({ userId, otp: res.data.otp, message: res.data.message });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate OTP');
    } finally { setOtpLoading(null); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  const ROLES = ['IT Admin', 'Project Manager', 'Site Engineer', 'Supervisor', 'Accountant', 'Store Keeper', 'Team Leader'];

  return (
    <div className="space-y-3" data-testid="users-tab">
      <div className="flex gap-3 items-end">
        <Select label="Filter by Tenant" value={selectedTenant} onChange={(e) => setSelectedTenant(e.target.value)} data-testid="user-tenant-filter">
          <option value="">All Tenants</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.company_name}</option>)}
        </Select>
        <span className="text-sm text-[#888] pb-2">{users.length} user(s)</span>
      </div>

      {/* OTP Result Banner */}
      {otpResult && (
        <div className="p-4 rounded-xl border-2 border-emerald-300 bg-emerald-50 space-y-2" data-testid="otp-result-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5 text-emerald-600" weight="fill" />
              <span className="text-sm font-bold text-emerald-800">One-Time Password Generated</span>
            </div>
            <button onClick={() => setOtpResult(null)} className="text-emerald-600 hover:text-emerald-800 text-xs font-medium" data-testid="otp-dismiss">Dismiss</button>
          </div>
          <div className="flex items-center gap-3">
            <code className="text-2xl font-mono font-bold text-emerald-700 tracking-wider bg-white px-4 py-2 rounded-lg border border-emerald-200" data-testid="otp-code">
              {otpResult.otp}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(otpResult.otp); }}
              className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
              data-testid="otp-copy-btn"
            >
              Copy
            </button>
          </div>
          <p className="text-xs text-emerald-700">{otpResult.message}</p>
        </div>
      )}

      <div className="space-y-2">
        {users.map(u => (
          <Card key={u.id} className="p-3" data-testid={`sa-user-${u.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{u.name}</span>
                  {u.role === 'Super Admin' && <Badge variant="primary">Super Admin</Badge>}
                  {!u.is_active && <Badge variant="danger">Disabled</Badge>}
                </div>
                <p className="text-xs text-[#888]">{u.email}</p>
              </div>
              {u.role !== 'Super Admin' && (
                <div className="flex items-center gap-2">
                  <select value={u.role} onChange={(e) => handleRoleChange(u.id, e.target.value)}
                    className="text-xs border rounded px-2 py-1" data-testid={`role-select-${u.id}`}>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button onClick={() => handleGenerateOtp(u.id)}
                    disabled={otpLoading === u.id}
                    className="p-1.5 text-[#888] hover:text-[#F59E0B] hover:bg-[#FEF3C7] rounded transition-colors" title="Generate OTP"
                    data-testid={`generate-otp-${u.id}`}>
                    {otpLoading === u.id ? (
                      <ArrowClockwise className="w-4 h-4 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4" />
                    )}
                  </button>
                  <button onClick={() => { setResetUserId(u.id); setNewPassword(''); setShowResetModal(true); }}
                    className="p-1.5 text-[#888] hover:text-[#002FA7] hover:bg-[#F0F4FF] rounded" title="Reset password" data-testid={`reset-pass-${u.id}`}>
                    <LockKey className="w-4 h-4" />
                  </button>
                  {u.is_active !== false ? (
                    <button onClick={() => handleDisable(u.id)} className="p-1.5 text-[#888] hover:text-[#FF3333] hover:bg-[#FEF2F2] rounded" title="Disable user" data-testid={`disable-${u.id}`}>
                      <UserCircleMinus className="w-4 h-4" />
                    </button>
                  ) : (
                    <button onClick={() => handleEnable(u.id)} className="p-1.5 text-[#888] hover:text-[#00C853] hover:bg-[#E8F5E9] rounded" title="Enable user" data-testid={`enable-${u.id}`}>
                      <UserCirclePlus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Modal isOpen={showResetModal} onClose={() => setShowResetModal(false)} title="Reset User Password">
        <div className="space-y-3">
          <Input label="New Password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password-input" />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowResetModal(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={resetSaving || !newPassword} data-testid="confirm-reset-btn">
              {resetSaving ? 'Resetting...' : 'Reset Password'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ==================== BACKUPS TAB ====================
function BackupsTab() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const r = await superAdminApi.listBackups(); setBackups(r.data); }
    catch {} finally { setLoading(false); }
  };

  const handleCreate = async () => {
    setCreating(true);
    try { await superAdminApi.createBackup(); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Backup failed'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (name) => {
    if (!window.confirm('Delete this backup permanently?')) return;
    try { await superAdminApi.deleteBackup(name); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleRestore = async (name) => {
    if (!window.confirm(`RESTORE from "${name}"? This will replace ALL current data. Are you absolutely sure?`)) return;
    setRestoring(name);
    try { await superAdminApi.restoreBackup(name, true); alert('Restore completed successfully!'); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Restore failed'); }
    finally { setRestoring(null); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-3" data-testid="backups-tab">
      <div className="flex justify-between items-center">
        <span className="text-sm font-semibold text-[#1A1F36]">{backups.filter(b => b.status === 'completed').length} Backup(s)</span>
        <Button size="sm" onClick={handleCreate} disabled={creating} data-testid="create-backup-btn">
          {creating ? <><ArrowClockwise className="w-4 h-4 mr-1 animate-spin" /> Creating...</> : <><Database className="w-4 h-4 mr-1" /> Create Backup</>}
        </Button>
      </div>
      {backups.length === 0 ? (
        <Card className="p-8 text-center text-[#888]">No backups yet</Card>
      ) : (
        <div className="space-y-2">
          {backups.map(b => (
            <Card key={b.id} className="p-3" data-testid={`backup-${b.backup_name}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Database className="w-5 h-5 text-[#1A1F36]" />
                  <div>
                    <p className="font-mono text-sm font-medium">{b.backup_name}</p>
                    <div className="flex gap-2 text-xs text-[#888]">
                      {b.size_mb && <span>{b.size_mb} MB</span>}
                      <span>{b.created_at ? new Date(b.created_at).toLocaleString('en-US') : ''}</span>
                      {b.action === 'restore' && <span className="text-[#002FA7]">Restore log</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={b.status === 'completed' ? 'success' : b.status === 'restored' ? 'primary' : b.status === 'deleted' ? 'default' : 'danger'}>{b.status}</Badge>
                  {b.status === 'completed' && !b.action && (
                    <>
                      <button onClick={() => handleRestore(b.backup_name)} disabled={restoring === b.backup_name}
                        className="p-1.5 text-[#888] hover:text-[#002FA7] hover:bg-[#F0F4FF] rounded text-xs" title="Restore" data-testid={`restore-${b.backup_name}`}>
                        {restoring === b.backup_name ? <ArrowClockwise className="w-4 h-4 animate-spin" /> : <ArrowClockwise className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleDelete(b.backup_name)}
                        className="p-1.5 text-[#888] hover:text-[#FF3333] hover:bg-[#FEF2F2] rounded" title="Delete" data-testid={`delete-backup-${b.backup_name}`}>
                        <Trash className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== AUDIT TAB ====================
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    superAdminApi.auditLog(200).then(r => setLogs(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-3" data-testid="audit-tab">
      <span className="text-sm font-semibold text-[#1A1F36]">{logs.length} Audit Entries</span>
      {logs.length === 0 ? (
        <Card className="p-8 text-center text-[#888]">No audit entries yet</Card>
      ) : (
        <div className="space-y-2">
          {logs.map(l => (
            <Card key={l.id} className="p-3" data-testid={`audit-${l.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="primary">{l.action}</Badge>
                    <span className="text-xs text-[#888]">{l.admin_email}</span>
                  </div>
                  {l.details && Object.keys(l.details).length > 0 && (
                    <p className="text-xs text-[#666] mt-1 font-mono">{JSON.stringify(l.details)}</p>
                  )}
                </div>
                <span className="text-xs text-[#888]">{l.timestamp ? new Date(l.timestamp).toLocaleString('en-US') : ''}</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
